import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ComponentName } from '../detailsDrawersHelpers';
import { PermissibleRender } from '../../PermissibleRender';
import { AttachmentQueue } from '../../AttachmentQueue';
import { AttachmentCTA } from '../../AttachmentCTA';
import { HIDE_RECORD_PERMISSIONS, Permission } from '../../../../store/GeneralStore';
import { useAttachments } from '../../../../store/Attachment';
import {
  Attachment,
  HiddenAssetSource,
  InventoryAsset,
  PortalUserData,
  SupportCasesItem,
  User
} from '../../../../types';
import { getSFHost } from '../../../../utils/helpers';
import { isSupportCase, useSupportCases } from '../../../../store/SupportCase';
import { isInventoryItem, useInventory } from '../../../../store/Inventory';
import { useWorkflowOrders } from '../../../../store/WorkflowOrder';
import { WorkflowOrder } from '../../../../Api/Orders/ordersApi';
import { Icon } from '../../../Atoms/Icon';
import { getUserSubject } from '../../../../store/User';
import { SKIP_ACCOUNT_ROLES } from '../../../../store/Role';
import { getQueuedAttachmentsSubject } from '../../../../store/Attachment';
import { deleteTeam, Team } from '../../../../store/Team';
import { CardButton } from '../../Buttons';
import { AccountData } from '../../../../store/Accounts';
import { ConfirmationModal } from '../../../Pages/AddInventory/ConfirmationModal';
import { HideConfirmationDialog, HideRecordButton } from '../../HideConfirmationDialog';
import { useS3Attachments } from '../../../../store/S3Attachment';
import { fetchProjects, Project } from '../../../../store/Project';
import { PriceBenchMark } from '../../../../store/PriceBenchMark';

export interface DrawerActionsProps {
  componentName?: ComponentName;
  data:
    | InventoryAsset
    | SupportCasesItem
    | PortalUserData
    | Team
    | AccountData
    | Project
    | PriceBenchMark
    | WorkflowOrder
    | null;
  setShow: (show: boolean) => void;
  // Which surface a hide/unhide action originates from. The Price Benchmarking
  // drawer reuses the inventory drawer but must hide records independently as
  // 'Benchmark'. Defaults to 'Inventory'.
  hiddenAssetSource?: HiddenAssetSource;
  // Overrides the componentName-mapped refresh — for surfaces whose rows don't come
  // from that store (the risk-mode Related Inventory drawer refetches its project).
  onRefresh?: () => void;
}

export const DrawerActions = ({ componentName, data, setShow, hiddenAssetSource, onRefresh }: DrawerActionsProps) => {
  const { uploadAttachments, cancelUpload, retryUpload, setParentId } = useAttachments();
  const { queuedS3Attachments, cancelS3Upload, updateS3Attachments, retryS3Upload, setS3EntityId, setS3EntityType } =
    useS3Attachments();
  const { fetchCases } = useSupportCases();
  const { fetchInventory, deleteInventoryById, createHiddenAsset, deleteHiddenAsset } = useInventory();
  const { fetchWorkflowOrders } = useWorkflowOrders();

  const [search] = useSearchParams();
  const navigate = useNavigate();
  const detailId = search.get('detailId');

  // TODO: abstract into role based wrapping component to conditionally show and hide based on roles/personas
  const [showLink, setShowLink] = useState<boolean>(false);
  const [userData, setUserData] = useState<User>();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [hideConfirmationModalOpen, setHideConfirmationModalOpen] = useState(false);
  const [queuedAttachments, setQueuedAttachments] = useState<Attachment[]>([]);

  const shouldShowLink = (user: User) => {
    const userShouldSeeLink = user?.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name));

    if (!userShouldSeeLink) return false;

    return !(componentName === 'inventory' && data && !(data as InventoryAsset).origin);
  };

  useEffect(() => {
    if (userData) setShowLink(shouldShowLink(userData));
  }, [userData, data?.id]);

  useEffect(() => {
    const userSub = getUserSubject().subscribe((user) => setUserData(user));
    const attachmentSubscription = getQueuedAttachmentsSubject().subscribe((queuedAttachments) => {
      setQueuedAttachments(queuedAttachments.filter((att) => att.parentId === detailId));
    });

    return () => {
      if (userSub) userSub.unsubscribe();
      if (attachmentSubscription) attachmentSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (data && data.id && componentName && ['support', 'inventory', 'workflow-order'].includes(componentName)) {
      if (!(data as InventoryAsset).origin) {
        setS3EntityId(data.id);
        setS3EntityType('OutsideInventory');
      } else {
        setParentId(data.id);
      }
    }
  }, [data?.id]);

  const deleteOutsideInventory = (id: string | null) => {
    if (id) {
      deleteInventoryById(id);
      fetchInventory(true);
      navigate('/inventory');
    }
  };

  const actionsClassName =
    'inline-flex items-center border-transparent overflow-hidden rounded-lg border-secondary-300';
  const attachmentQueueClassName = 'flex absolute top-20 right-0 w-max bg-white z-10 py-4';
  const isHidden = (data as InventoryAsset)?.isHidden;
  const hideSource: HiddenAssetSource = hiddenAssetSource ?? (componentName === 'project' ? 'Project' : 'Inventory');
  // A project carries its portal account id nested; inventory rows carry a flat Salesforce one.
  const hideAccountId =
    hideSource === 'Project' ? (data as Project)?.account?.id : (data as InventoryAsset)?.account_id;

  const handleHideUnhide = async () => {
    setHideConfirmationModalOpen(false);
    setShow(false);
    const actionFunc = isHidden ? deleteHiddenAsset : createHiddenAsset;
    // Navigating after a failed call would read as success while the record is still there.
    if (!(await actionFunc((data as InventoryAsset).id, hideAccountId || '', hideSource))) return;
    // Inventory hides navigate to the inventory list; Benchmark hides stay on the
    // Cost Intelligence page (the drawer simply closes and the row is refetched out).
    if (!isHidden && hideSource === 'Inventory') navigate('/inventory');
    // A hidden project is gone from the list its page is built from, so the list is the only place left to land.
    if (!isHidden && hideSource === 'Project') navigate('/projects');
  };

  const renderHideUnhideSection = () => {
    // Benchmark hides apply to both SF and Portal Assets; Inventory hides only apply
    // to SF assets (Portal Assets are not hideable from the Inventory table).
    if (hideSource === 'Inventory' && (data as InventoryAsset).origin === false) return null;

    return (
      <PermissibleRender requiredPermissions={HIDE_RECORD_PERMISSIONS}>
        <div className={`${actionsClassName} pr-4`}>
          <HideRecordButton
            hidden={!!isHidden}
            onClick={() => setHideConfirmationModalOpen(true)}
            testId="hideUnhideButton"
            sfRecordLabel={hideSource === 'Project' ? 'Project' : 'Asset'}
          />
          <HideConfirmationDialog
            open={hideConfirmationModalOpen}
            onClose={() => setHideConfirmationModalOpen(false)}
            onConfirm={handleHideUnhide}
            type={isHidden ? 'Unhide' : 'Hide'}
            recordType={hideSource === 'Project' ? 'Project' : 'Inventory'}
          />
        </div>
      </PermissibleRender>
    );
  };

  const defaultButtons = () => {
    const fetchActions = {
      support: {
        fetchFunc: fetchCases,
        deleteFunc: null,
        segment: 'Case'
      },
      inventory: {
        fetchFunc: fetchInventory,
        deleteFunc: deleteOutsideInventory,
        segment: 'Asset'
      },
      project: {
        fetchFunc: fetchProjects,
        deleteFunc: null,
        segment: 'Project'
      },
      'project-asset': {
        fetchFunc: fetchInventory,
        deleteFunc: null,
        segment: 'Asset'
      },
      'workflow-order': {
        fetchFunc: () => fetchWorkflowOrders(true),
        deleteFunc: null,
        segment: 'Opportunity'
      }
    };
    const urlSegment = fetchActions?.[componentName as keyof typeof fetchActions].segment;
    const linkId =
      isSupportCase(data) || isInventoryItem(data) ? data.sfId : (data as WorkflowOrder)?.sf_id || detailId;
    const safeSegment = encodeURIComponent(urlSegment);
    const safeLinkId = encodeURIComponent(String(linkId));
    const url = `${getSFHost()}/lightning/r/${safeSegment}/${safeLinkId}/view`;
    const buttonClassName = 'self-center !bg-secondary-300 hover:!bg-secondary-200 !rounded-none h-7 w-9';

    return (
      <>
        <Tooltip
          title="Refresh"
          placement="top">
          <IconButton
            className={buttonClassName}
            data-testid="refreshButton"
            onClick={() =>
              onRefresh ? onRefresh() : fetchActions?.[componentName as keyof typeof fetchActions].fetchFunc(true)
            }>
            <Icon type="refreshWhite" />
          </IconButton>
        </Tooltip>
        {showLink && (
          <Tooltip
            title="Open in SF"
            placement="top">
            <IconButton
              className={buttonClassName}
              data-testid="openInSalesforceButton"
              href={url}
              target="_blank"
              rel="noopener noreferrer">
              <Icon type="externalWhite" />
            </IconButton>
          </Tooltip>
        )}
        {(data as InventoryAsset).origin === false && (
          <PermissibleRender requiredPermissions={[Permission.DELETE_OUTSIDE_INVENTORY]}>
            <>
              <IconButton
                className={buttonClassName}
                data-testid="deleteButton"
                onClick={() => setConfirmationModalOpen(true)}>
                <Icon
                  type="trashWhite"
                  className="p-2"
                />
              </IconButton>
              <Dialog
                open={confirmationModalOpen}
                onClose={() => setConfirmationModalOpen(false)}>
                <IconButton
                  aria-label="close"
                  onClick={() => setConfirmationModalOpen(false)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 4,
                    color: (theme) => theme.palette.grey[500]
                  }}>
                  <CloseIcon />
                </IconButton>
                <DialogContent>
                  <ConfirmationModal
                    close={() => setConfirmationModalOpen(false)}
                    items={0}
                    ctaAction={() => fetchActions?.[componentName as keyof typeof fetchActions].deleteFunc?.(detailId)}
                    type="Delete"
                    wrapperClassName="max-w-[30vw] min-w-[20vw]"
                    modalClassName="pb-2 text-center"
                  />
                </DialogContent>
              </Dialog>
            </>
          </PermissibleRender>
        )}
      </>
    );
  };

  const getActions = () => {
    switch (componentName) {
      case 'project-asset':
      case 'inventory':
        return (
          <>
            {renderHideUnhideSection()}
            <div className={actionsClassName}>
              <PermissibleRender requiredPermissions={[Permission.ADD_ATTACHMENTS_INVENTORY]}>
                <AttachmentCTA
                  addAttachments={(data as InventoryAsset).origin ? uploadAttachments : updateS3Attachments}
                />
              </PermissibleRender>
              {defaultButtons()}
            </div>
            <div className={attachmentQueueClassName}>
              <PermissibleRender requiredPermissions={[Permission.ADD_ATTACHMENTS_INVENTORY]}>
                <AttachmentQueue
                  queuedAttachments={(data as InventoryAsset).origin ? queuedAttachments : queuedS3Attachments}
                  cancelUpload={(data as InventoryAsset).origin ? cancelUpload : cancelS3Upload}
                  retryUpload={(data as InventoryAsset).origin ? retryUpload : retryS3Upload}
                />
              </PermissibleRender>
            </div>
          </>
        );
      case 'project':
        return (
          <>
            {renderHideUnhideSection()}
            <div className={actionsClassName}>{defaultButtons()}</div>
          </>
        );
      case 'workflow-order':
        return (
          <>
            <div className={actionsClassName}>{defaultButtons()}</div>
          </>
        );
      case 'support':
        return (
          <>
            <div className={actionsClassName}>
              <PermissibleRender requiredPermissions={[Permission.ADD_CASES_ATTACHMENTS]}>
                <AttachmentCTA addAttachments={uploadAttachments} />
              </PermissibleRender>
              {defaultButtons()}
            </div>
            <div className={attachmentQueueClassName}>
              <PermissibleRender requiredPermissions={[Permission.ADD_CASES_ATTACHMENTS]}>
                <AttachmentQueue
                  queuedAttachments={queuedAttachments}
                  cancelUpload={cancelUpload}
                  retryUpload={retryUpload}
                />
              </PermissibleRender>
            </div>
          </>
        );
      case 'team':
        return (
          <>
            <div className={actionsClassName}>
              {!!data?.id && (
                <Tooltip
                  title="Delete"
                  placement="top">
                  <CardButton
                    data-testid="delete-team"
                    data-cy="drawer-delete-team-btn"
                    icon="trash"
                    className="self-center !bg-secondary-300 hover:!bg-secondary-200 h-7 px-2"
                    onClick={() => {
                      deleteTeam(data.id);
                      setShow(false);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </>
        );
      default:
        return <></>;
    }
  };

  return getActions();
};
